import React, { RefObject, useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { regular } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "components/modal/Modal";
import { Button } from "components/v2/button/Button";
import {
	TRANSLATION_ERROR_MESSAGE_ENGLISH,
	TRANSLATION_ERROR_MESSAGE_SPANISH,
	TRANSLATION_ERROR_MODAL_TITLE,
} from "utilities/Constant";
import "./translation-error-modal.scss";

export interface TranslationErrorModalProps {
	readonly onClose: () => void;
	readonly returnFocusRef?: RefObject<HTMLElement | null>;
}

const FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function TranslationErrorModal({ onClose, returnFocusRef }: TranslationErrorModalProps) {
	const modalRef = useRef<HTMLDialogElement>(null);

	const closeModal = useCallback(() => {
		onClose();
		window.setTimeout(() => returnFocusRef?.current?.focus(), 0);
	}, [onClose, returnFocusRef]);

	useEffect(() => {
		document.body.classList.add("preferred-language-modal-open");
		return () => document.body.classList.remove("preferred-language-modal-open");
	}, []);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (!modalRef.current) {
				return;
			}

			if (event.key === "Escape") {
				closeModal();
				return;
			}

			if (event.key !== "Tab") {
				return;
			}

			const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);

			if (!focusableElements.length) {
				return;
			}

			const firstFocusableElement = focusableElements[0];
			const lastFocusableElement = focusableElements[focusableElements.length - 1];

			if (event.shiftKey && document.activeElement === firstFocusableElement) {
				event.preventDefault();
				lastFocusableElement.focus();
			} else if (!event.shiftKey && document.activeElement === lastFocusableElement) {
				event.preventDefault();
				firstFocusableElement.focus();
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [closeModal]);

	useEffect(() => {
		const firstFocusableElement = modalRef.current?.querySelector<HTMLElement>("button");
		firstFocusableElement?.focus();
	}, []);

	const customHeader = (
		<div className="translation-error-modal__header">
			<h3 id="translation-error-modal-title" className="translation-error-modal__title">
				<span className="GlobalLinkNoTx">{TRANSLATION_ERROR_MODAL_TITLE}</span>
			</h3>
			<button
				type="button"
				className="translation-error-modal__close-btn"
				aria-label="Close translation error modal"
				onClick={closeModal}
			>
				<FontAwesomeIcon icon={regular("xmark")} />
			</button>
		</div>
	);

	const customBody = (
		<div className="translation-error-modal__body">
			<div className="translation-error-modal__segment">
				<p
					id="translation-error-modal-description"
					className="translation-error-modal__description GlobalLinkNoTx"
				>
					{TRANSLATION_ERROR_MESSAGE_ENGLISH}
				</p>
			</div>
			<div className="translation-error-modal__segment">
				<p className="translation-error-modal__description">
					{TRANSLATION_ERROR_MESSAGE_SPANISH}
				</p>
			</div>
		</div>
	);

	const footer = (
		<div className="translation-error-modal__footer">
			<Button
				label="Close"
				className="translation-error-modal__close-cta"
				onClick={closeModal}
			/>
		</div>
	);

	return ReactDOM.createPortal(
		<dialog
			open
			className="translation-error-modal"
			ref={modalRef}
			aria-labelledby="translation-error-modal-title"
			aria-describedby="translation-error-modal-description"
		>
			<Modal customHeader={customHeader} customBody={customBody} footer={footer} footerContentPosition="right" />
		</dialog>,
		document.body,
	);
}
